import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Textarea } from "@/shared/components/ui/textarea";
import { resolveMutationErrorMessage } from "@/shared/api/error-message";
import { useResolveDriftAlert } from "./use-resolve-drift-alert";
import { RESOLUTION_NOTE_MAX_LENGTH } from "./constants";
import type { DriftAlert } from "./types";

interface ResolveDriftAlertDialogProps {
  readonly alert: DriftAlert | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSuccess: () => void;
}

export function ResolveDriftAlertDialog({
  alert,
  open,
  onOpenChange,
  onSuccess,
}: ResolveDriftAlertDialogProps) {
  const { t } = useTranslation();
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const resolve = useResolveDriftAlert();

  useEffect(() => {
    if (open) {
      setNote("");
      setError(null);
    }
  }, [open]);

  if (!alert) return null;

  const handleClose = (next: boolean) => {
    if (!next && resolve.isPending) return;
    onOpenChange(next);
  };

  const handleSubmit = () => {
    setError(null);
    resolve.mutate(
      { id: alert.id, note },
      {
        onSuccess: () => onSuccess(),
        onError: (err) => {
          setError(
            resolveMutationErrorMessage(err, t, {
              forbiddenKey: "subscriptions.driftAlerts.errors.forbidden",
              serverPrefixKey: "subscriptions.driftAlerts.errors.serverPrefix",
              genericKey: "subscriptions.driftAlerts.resolve.error",
            }),
          );
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="resolve-drift-alert-dialog">
        <DialogHeader>
          <DialogTitle>{t("subscriptions.driftAlerts.resolve.title")}</DialogTitle>
          <DialogDescription>
            {t("subscriptions.driftAlerts.resolve.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-1.5">
            <label
              htmlFor="resolve-drift-alert-note"
              className="font-sans text-sm font-medium text-foreground"
            >
              {t("subscriptions.driftAlerts.resolve.noteLabel")}
            </label>
            <Textarea
              id="resolve-drift-alert-note"
              data-testid="resolve-drift-alert-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={RESOLUTION_NOTE_MAX_LENGTH}
              rows={3}
              placeholder={t("subscriptions.driftAlerts.resolve.notePlaceholder")}
              disabled={resolve.isPending}
            />
            <p className="font-sans text-xs font-normal text-muted-foreground">
              {t("subscriptions.driftAlerts.resolve.noteHint")}
            </p>
          </div>

          {error && (
            <p
              data-testid="resolve-drift-alert-error"
              role="alert"
              className="font-sans text-sm font-medium text-destructive"
            >
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={resolve.isPending}>
            {t("subscriptions.driftAlerts.resolve.cancel")}
          </Button>
          <Button
            data-testid="confirm-resolve-drift-alert-button"
            onClick={handleSubmit}
            disabled={resolve.isPending}
          >
            {resolve.isPending
              ? t("subscriptions.driftAlerts.resolve.pending")
              : t("subscriptions.driftAlerts.resolve.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
